<template>
  <form
    class="main-cart critical-component-clear"
    action="/cart"
    method="post"
  >
    <div
      class="main-cart__container component-section container"
      :class="{'main-cart__container--full': cartItems.length > 0}"
    >
      <div
        v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
        class="grid"
      >
        <div class="col xs6 l1-6">
          <div class="main-cart__title-wrapper">
            <h1
              id="template-title"
              class="main-cart__title text-heading-l text-heading-xl-desktop"
              v-text="$string('cart.my_bag')"
            />

            <btn
              v-if="$variable('routes.wishlist.url')"
              class="text-button-2xs"
              :label="$string('cart.view_wishlist')"
              :url="$variable('routes.wishlist.url')"
              modifiers="text"
              :show-icon="false"
            />
          </div>

          <progress-bar
            v-if="displayFreeShipping"
            class="main-cart__progress-bar"
          />

          <div class="main-cart__line-items">
            <line-item
              v-for="(item) of cartItems"
              :key="getCartItemKey(item)"
              block-class="main-cart"
              :item="item"
            />
          </div>

          <div
            v-if="trustpilotWidget"
            class="main-cart__reviews"
            v-html="trustpilotWidget"
          />
        </div>

        <aside class="main-cart__sidebar col xs6 l8-12 offset-left-l">
          <cart-summary
            class="main-cart__summary"
            :free-delivery="cartThresholdMet"
            :gift-wrapping="giftWrapping"
            :navigation-items="navigationItems"
            :payments="payments"
          />
        </aside>
      </div>

      <div
        v-else
        class="main-cart__empty"
      >
        <h1
          class="main-cart__empty-text text-heading-l text-heading-xl-desktop"
          v-text="$string('cart.my_bag')"
        />

        <p
          class="main-cart__empty-description text-body-s"
          v-text="$string('cart.empty.text')"
        />

        <div class="main-cart__buttons">
          <btn
            class="main-cart__continue"
            :label="$string('cart.continue_browsing')"
            :url="$variable('routes.root')"
            :show-icon="false"
          />

          <btn
            class="main-cart__view-wishlist"
            :label="$string('cart.view_wishlist')"
            :url="$variable('routes.wishlist.url')"
            modifiers="outlined"
            :show-icon="false"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script>

/**
 * Vue: Main cart (main-cart)
 * -----------------------------------------------------------------------------
 * Cart page with line items.
 *
 * @param {Object} giftWrapping - Object of gift wrapping feature.
 * @param {Array} navigationItems - Array of navigation link for the cart.
 * @param {Array} payments - Array of payment icons and type.
 * @param {String} trustpilotWidget - Trustpilot quote reviews.
 *
 */
import { mapState } from 'vuex'

import Btn from '~global/btn/btn'
import CartSummary from '~async/main-cart/cart-summary'
import LineItem from '~async/line-item/line-item'
import ProgressBar from '~async/progress-bar/progress-bar'

export default {
  name: 'MainCart',

  components: {
    Btn,
    CartSummary,
    LineItem,
    ProgressBar,
  },

  props: {
    giftWrapping: {
      type: Object,
      default: () => ({
        enable: false,
        product: {},
      }),
    },
    navigationItems: {
      type: Array,
      default: () => [],
    },
    payments: {
      type: Array,
      default: () => [],
      required: true,
    },
    trustpilotWidget: {
      type: String,
      default: '',
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.purifiedCartItems
      }

      /*
       * If the product is being added is gift wrapping, then return the cart.
       */
      if (
        this.cartStatus.items?.length === 1 &&
        this.cartStatus.items[0] === this.giftWrapping.product?.variants[0]?.id) {
        return this.purifiedCartItems
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.purifiedCartItems.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.purifiedCartItems,
      ]
    },

    /**
     * Is free shipping threshold met.
     * @returns {boolean}
     */
    cartThresholdMet() {
      return (
        cnvs.freeShipping.enable &&
        this.cart?.total_price >= cnvs.freeShipping.threshold
      )
    },

    /**
     * Compute free shipping status.
     * @return {Boolean}
     */
    displayFreeShipping() {
      return this.cart.item_count > 0 && cnvs.freeShipping.enable
    },

    /**
     * Compute cart items without gift wrapping.
     * @return {Array}
     */
    purifiedCartItems() {
      return this.cart.items.filter((item) => {
        return item.variant_id !== this.$variable('giftWrapping.productId')
      })
    },
  },

  methods: {

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @returns {String}
     */
    getCartItemKey(item) {
      if (!item) {
        return 'cart-line-item-loading'
      }

      return `cart-line-item-${item.key}`
    },
  },
}
</script>

<style lang="scss">
@import './main-cart';
</style>
